import { getPlanTrainUnitTextPaths } from "../core/planTrainUnitTextCalculations";
import { Path as PathKonva, Text, TextPath } from "react-konva";
import { useEffect, useMemo, useRef } from "react";
import { useTheme } from "@shared/EDS/contexts/useTheme";
import Konva from "konva";
import { PointHelper } from "@opstel/geo/helpers/PointHelper";
import { Point } from "@opstel/geo/Point";

interface PlanTrainUnitTextProps {
  readonly backgroundColor: string;
  readonly backgroundHeight: number;
  readonly mainText: string;
  readonly points: Point[];
  readonly subText: string;
  readonly textColor: string;
  readonly trackLineWidth: number;
}

export const PlanTrainUnitText = ({
  backgroundColor,
  backgroundHeight,
  mainText,
  points,
  subText,
  textColor,
  trackLineWidth,
}: PlanTrainUnitTextProps) => {
  const { theme } = useTheme();

  const mainTextRef = useRef<Konva.Text>(null);
  const subTextRef = useRef<Konva.Text>(null);
  const mainTextPathRef = useRef<Konva.TextPath>(null);
  const subTextPathRef = useRef<Konva.TextPath>(null);
  const bgPathRef = useRef<Konva.Path>(null);

  const textPoints = useMemo(() => PointHelper.rotateIfUpsideDown(points), [points]);

  const setMainTextWidth = (width: number) => {
    // if the main text is wider than the path, we shorten the main text to fit the path. This triggers the text to ellipsis if needed.
    if (mainTextRef.current && mainTextRef.current.width() > width) {
      mainTextRef.current.setAttrs({
        width,
      });
    }
  };

  function getMainText(): string {
    return mainTextRef.current!.textArr.length > 0
      ? mainTextRef.current!.textArr[0].text
      : // NOTE: weird ref attribute accessing.
        // oxlint-disable-next-line typescript/no-unsafe-member-access
        (mainTextRef.current!.attrs.text as string);
  }

  useEffect(() => {
    if (
      !mainTextRef.current ||
      !subTextRef.current ||
      !mainTextPathRef.current ||
      !subTextPathRef.current ||
      !bgPathRef.current
    ) {
      return;
    }

    const mainTextWidth = mainTextRef.current.width();
    const typeTextWidth = subTextRef.current.width();

    const { mainTextPath, subTextPath, bgPath } = getPlanTrainUnitTextPaths(
      mainTextWidth,
      typeTextWidth,
      textPoints,
      trackLineWidth,
    );

    setMainTextWidth(mainTextPath.length);

    mainTextPathRef.current.setAttrs({
      data: mainTextPath.toDataString(),
      text: getMainText(),
    });

    subTextPathRef.current.setAttrs({
      data: subTextPath.toDataString(),
    });

    bgPathRef.current.setAttrs({
      data: bgPath.toDataString(),
    });
  }, [backgroundHeight, textPoints, trackLineWidth]);

  const fontSize = theme.typography.sizeXS.value * (backgroundHeight / 12);

  return (
    <>
      {
        // The Path element is used to color the background of the text.
      }
      <PathKonva ref={bgPathRef} stroke={backgroundColor} strokeWidth={backgroundHeight} />
      {
        // The following two Text elements only exists for calculating the text widths. And for the
        // typeText to trigger the ellipsis if needed.
      }
      <Text
        ref={mainTextRef}
        text={mainText}
        x={points[0].x}
        y={points[0].y}
        visible={false}
        fontSize={fontSize}
        ellipsis={true}
        wrap="none"
        fontFamily={theme.typography.family}
        fontStyle={theme.typography.weightBold.toString()}
      />
      <Text
        ref={subTextRef}
        text={subText}
        x={points[0].x}
        y={points[0].y}
        visible={false}
        fontSize={fontSize}
        fontFamily={theme.typography.family}
        fontStyle={theme.typography.weightNormal.toString()}
      />
      {
        // The following two TextPath elements are used to show the text along the path of the PlanTrainUnit.
      }
      <TextPath
        offsetY={-1}
        ref={mainTextPathRef}
        align="center"
        text={mainText}
        fontSize={fontSize}
        fontFamily={theme.typography.family}
        fontStyle={theme.typography.weightBold.toString()}
        fill={textColor}
      />
      <TextPath
        offsetY={-1}
        ref={subTextPathRef}
        align="center"
        text={subText}
        fontSize={fontSize}
        fontFamily={theme.typography.family}
        fontStyle={theme.typography.weightNormal.toString()}
        fill={textColor}
      />
    </>
  );
};
